import React, { useState, useEffect } from 'react';

const EyeIcon = ({ open, onClick }) => (
  <button type="button" onClick={onClick} data-testid="toggle-password" style={{
    position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
    color: 'rgba(196, 137, 74, 0.50)', fontSize: '16px', lineHeight: 1,
  }}>
    {open ? (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
      </svg>
    ) : (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </svg>
    )}
  </button>
);

const LoginView = ({ onLogin, onNavigate }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('remembered_credentials');
    if (saved) {
      try {
        const { email: e, password: p } = JSON.parse(atob(saved));
        setEmail(e);
        setPassword(p);
        setRememberMe(true);
      } catch { /* ignore corrupt data */ }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Login failed');
        return;
      }
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user_email', data.email);
      localStorage.setItem('user_id', data.user_id);
      if (data.display_name) localStorage.setItem('user_name', data.display_name);

      if (rememberMe) {
        localStorage.setItem('remembered_credentials', btoa(JSON.stringify({ email, password })));
      } else {
        localStorage.removeItem('remembered_credentials');
      }

      onLogin();
    } catch (err) {
      setError('Could not connect to server');
    }
  };

  return (
    <div className="velvet-void" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '0 32px',
    }} data-testid="login-view">
      <div style={{ width: '100%', maxWidth: '400px' }} className="fade-in">
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div className="breathing-sphere" style={{ margin: '0 auto' }} />
        </div>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 className="font-serif" style={{ fontSize: '2.25rem', color: 'rgba(255, 243, 225, 0.82)', marginBottom: '8px' }}>Eunoia Presence</h1>
          <p style={{ color: 'rgba(255, 243, 225, 0.35)', fontSize: '0.875rem' }}>Welcome back</p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {error && (
            <div style={{ textAlign: 'center', fontSize: '0.875rem', color: 'rgba(196, 137, 74, 0.8)' }}>{error}</div>
          )}
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email" className="velvet-input" />
          <div style={{ position: 'relative' }}>
            <input type={showPassword ? 'text' : 'password'} placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="velvet-input"
              style={{ paddingRight: '44px' }} />
            <EyeIcon open={showPassword} onClick={() => setShowPassword(!showPassword)} />
          </div>

          <label style={{
            display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
            fontFamily: "'EB Garamond', Georgia, serif", fontSize: '14px',
            color: 'rgba(255, 243, 225, 0.55)', padding: '0 4px',
          }} data-testid="remember-me-label">
            <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
              data-testid="remember-me-checkbox"
              style={{ accentColor: 'rgba(196, 137, 74, 0.6)', width: '16px', height: '16px' }} />
            remember me
          </label>

          <button type="submit" className="velvet-btn" data-testid="login-submit" style={{ marginTop: '8px' }}>Continue</button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <button onClick={() => onNavigate('register')} data-testid="goto-register"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255, 243, 225, 0.35)', fontSize: '0.875rem' }}>
            New here? <span style={{ textDecoration: 'underline', color: 'rgba(255, 243, 225, 0.55)' }}>Create one</span>
          </button>
        </div>

        <p style={{
          textAlign: 'center', marginTop: '48px',
          fontFamily: "'EB Garamond', Georgia, serif", fontSize: '12px',
          color: 'rgba(255, 243, 225, 0.25)',
        }}>a product of Divergent Nutritional Therapy LLC</p>
      </div>
    </div>
  );
};

export default LoginView;
